import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import { assertStatus } from "@mittwald/api-client-NEXT";
import { checkForValidationErrors } from "../../../base/index.js";
import type { ContainerBehaviors } from "./types.js";

export const apiContainerBehaviors = (
  client: MittwaldAPIV2Client,
): ContainerBehaviors => ({
  find: async (containerId, stackId) => {
    const response = await client.container.getService({
      serviceId: containerId,
      stackId,
    });
    if (response.status === 200) {
      return response.data;
    }
    assertStatus(response, 404);
  },

  list: async (projectId) => {
    const response = await client.container.listServices({
      projectId,
    });
    assertStatus(response, 200);
    return {
      items: response.data,
    };
  },

  create: async (projectId, data) => {
    const response = await client.container.updateStack({
      stackId: projectId,
      data,
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 200);
    return response.data;
  },

  delete: async (serviceName, stackId) => {
    const response = await client.container.updateStack({
      stackId,
      data: { services: { [serviceName]: {} } },
    });
    assertStatus(response, 200);
  },

  update: async (stackId, data) => {
    const response = await client.container.updateStack({
      stackId,
      data,
    });
    assertStatus(response, 200);
    return response.data;
  },

  start: async (containerId, stackId) => {
    const response = await client.container.startService({
      serviceId: containerId,
      stackId,
    });
    assertStatus(response, 204);
  },

  stop: async (containerId, stackId) => {
    const response = await client.container.stopService({
      serviceId: containerId,
      stackId,
    });
    assertStatus(response, 204);
  },

  restart: async (containerId, stackId) => {
    const response = await client.container.restartService({
      serviceId: containerId,
      stackId,
    });
    assertStatus(response, 204);
  },

  recreate: async (containerId, stackId) => {
    const response = await client.container.recreateService({
      serviceId: containerId,
      stackId,
    });
    assertStatus(response, 204);
  },

  getLog: async (containerId, stackId) => {
    const response = await client.container.getServiceLogs({
      serviceId: containerId,
      stackId,
    });
    assertStatus(response, 200);
    return response.data;
  },

  getImageMeta: async (imageRef, projectId, generateAiData) => {
    const response = await client.container.getContainerImageConfig({
      queryParameters: {
        imageReference: imageRef,
        useCredentialsForProjectId: projectId,
        generateAiData,
      },
    });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }
    assertStatus(response, 200);
    return response.data;
  },
});
